import { Component } from "@theme/component";

const FORMATS = new Set(["loose", "sachets"]);
const HOVER_MEDIA_QUERY = "(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)";
// The visual swap waits for the alternate packshot up to this long, then proceeds regardless.
const PACKSHOT_SWAP_TIMEOUT_MS = 1200;
// On a hovered card it also waits (less patiently: it is decorative) for the alternate hover photo,
// so the card lands on it instead of flashing the packshot first.
const LIFESTYLE_SWAP_TIMEOUT_MS = 400;
const PREFETCH_ROOT_MARGIN = "240px 0px";

/** The two per-format images: an inert template until materialized, then a live `<img>`. */
const PACKSHOT = { template: "template[data-packshot-image-template]", image: "img.tea-card__image--packshot" };
const LIFESTYLE = { template: "template[data-hover-image-template]", image: "img.tea-card__image--lifestyle" };

/** @type {IntersectionObserver | undefined} */
let visibilityObserver;

/**
 * @typedef {Object} TeaCardRefs
 * @property {HTMLAnchorElement} titleLink - The single keyboard tab-stop link
 * @property {HTMLButtonElement[]} formatButtons - Tea Sachets/Loose Leaf toggle pills (absent on single-format products)
 * @property {HTMLAnchorElement} [mediaLink] - Mouse-only image overlay link
 * @property {HTMLElement} [media] - The image well
 */

/**
 * The Wistearia product card. `data-active-format` is the single source of
 * state: CSS derives image/price/label visibility, this component derives everything else
 * (aria-pressed, the nested form's variant id, link hrefs). The format toggle is deliberately
 * self-contained — no `variant:update` (it would cross-contaminate sibling forms in a grid).
 *
 * Cart flow stays Horizon-native: the nested ProductFormComponent owns the request lock and
 * success duration. The button's `data-added` state drives the confirmation treatment in CSS,
 * keeping feedback synchronized with the transaction instead of running a second card timer.
 *
 * @extends {Component<TeaCardRefs>}
 */
class TeaCardComponent extends Component {
  requiredRefs = ["titleLink"];

  /** @type {AbortController} */
  #controller = new AbortController();

  #hoverMediaRequested = false;

  /** @type {WeakSet<HTMLImageElement>} */
  #trackedLifestyleImages = new WeakSet();

  /** Warmed alternate packshots and hover photos, keyed by kind and format, held so the browser keeps them cached. */
  /** @type {Map<string, HTMLImageElement>} */
  #imageProbes = new Map();

  /** @type {number | undefined} */
  #prefetchHandle;

  /** Monotonic token so a slower, earlier format request never overrides a later one. */
  #formatRequest = 0;

  #onHoverIntent = () => {
    this.#hoverMediaRequested = true;
    this.#loadLifestyleImage(this.dataset.activeFormat ?? "sachets");
    this.prefetchInactivePackshots();
  };

  /**
   * The pointer reaching the format pills is a coming switch. Deliberately not gated on
   * `#hoverMediaRequested`: `pointerover` fires just before the card's `pointerenter`, so a pointer
   * entering the card directly on a pill would otherwise be missed. (`:hover` cannot stand in for
   * it: Firefox only updates it after pointer events.) The listener exists only where fine-pointer
   * hover applies.
   * @param {PointerEvent} event
   */
  #onPointerOver = event => {
    if (event.target instanceof Element && event.target.closest(".tea-card__formats")) {
      this.prefetchInactiveLifestyles();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#trackedLifestyleImages = new WeakSet();
    if (globalThis.matchMedia?.(HOVER_MEDIA_QUERY).matches) {
      const { signal } = this.#controller;
      this.addEventListener("pointerenter", this.#onHoverIntent, { once: true, signal });
      this.addEventListener("pointerover", this.#onPointerOver, { signal });
    }
    this.#materializePackshotImage(this.dataset.activeFormat ?? "sachets");
    if (this.#hoverMediaRequested) {
      this.#loadLifestyleImage(this.dataset.activeFormat ?? "sachets");
    }
    observeVisibility(this);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#materializePackshotImage(this.dataset.activeFormat ?? "sachets");
    if (this.#hoverMediaRequested) {
      this.#loadLifestyleImage(this.dataset.activeFormat ?? "sachets");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    visibilityObserver?.unobserve(this);
    this.#cancelPackshotPrefetch();
  }

  /**
   * Schedules the inactive packshot prefetch for the browser's next idle period (bounded),
   * so it never competes with the page's own above-the-fold loading.
   */
  schedulePackshotPrefetch() {
    if (this.#prefetchHandle !== undefined) {
      return;
    }
    const run = () => {
      this.#prefetchHandle = undefined;
      if (this.isConnected) {
        this.prefetchInactivePackshots();
      }
    };
    this.#prefetchHandle =
      "requestIdleCallback" in globalThis ? requestIdleCallback(run, { timeout: 2000 }) : setTimeout(run, 1000);
  }

  #cancelPackshotPrefetch() {
    if (this.#prefetchHandle === undefined) {
      return;
    }
    if ("cancelIdleCallback" in globalThis) {
      cancelIdleCallback(this.#prefetchHandle);
    } else {
      clearTimeout(this.#prefetchHandle);
    }
    this.#prefetchHandle = undefined;
  }

  /**
   * Warms the HTTP cache with each inactive format's packshot at low priority, using the
   * exact responsive candidate the real `<img>` will pick (same srcset/sizes). The inert
   * template stays untouched, so nothing is added to the DOM and nothing renders twice.
   */
  prefetchInactivePackshots() {
    if (!this.refs.formatButtons?.length) {
      return;
    }
    const activeFormat = this.dataset.activeFormat;
    for (const media of this.querySelectorAll(".tea-card__format-media[data-format]")) {
      const format = media instanceof HTMLElement ? (media.dataset.format ?? "") : "";
      const eligible = FORMATS.has(format) && format !== activeFormat && !this.#imageProbes.has(`packshot:${format}`);
      const source = eligible ? imageSource(media, PACKSHOT) : null;
      if (media instanceof HTMLElement && source) {
        this.#imageProbes.set(`packshot:${format}`, createImageProbe(source));
        media.dataset.packshotPrefetched = "";
      }
    }
  }

  /**
   * Same warm-up for each inactive format's hover photo, started once the pointer reaches the
   * format pills (the strongest sign of a coming switch), so a hovered card can land on the new
   * format's photo at once. Fine-pointer hover only: touch devices never request hover media.
   */
  prefetchInactiveLifestyles() {
    if (!this.refs.formatButtons?.length) {
      return;
    }
    const activeFormat = this.dataset.activeFormat;
    for (const media of this.querySelectorAll(".tea-card__format-media[data-format]")) {
      const format = media instanceof HTMLElement ? (media.dataset.format ?? "") : "";
      const eligible = FORMATS.has(format) && format !== activeFormat && !this.#imageProbes.has(`lifestyle:${format}`);
      const source = eligible ? imageSource(media, LIFESTYLE) : null;
      if (source) {
        this.#imageProbes.set(`lifestyle:${format}`, createImageProbe(source));
      }
    }
  }

  /**
   * Applies a customer-selected display format to this card only. Controls, links, and
   * cart identity update at once; the visual media/price swap waits until the target
   * packshot is ready (bounded), so the well never flashes empty while it loads. On a hovered
   * card it also waits (briefly) for the target's hover photo: the card then lands on the photo
   * the hover rule shows anyway, instead of painting the packshot and fading to it.
   * @param {string} format - 'loose' | 'sachets'
   */
  async #setFormat(format) {
    if (!FORMATS.has(format)) {
      return;
    }

    // Validate BEFORE committing any state: a customer choice that this card
    // has no variant for (single-format products) must be a no-op — flipping
    // data-active-format first would hide the card's only media/price with no way back.
    let variantId = "";
    let available = true;
    let matched = false;
    for (const button of this.refs.formatButtons ?? []) {
      if (button.dataset.formatValue === format) {
        matched = true;
        if (button.dataset.variantId) {
          variantId = button.dataset.variantId;
        }
        available = button.dataset.available !== "false";
      }
    }
    if (!matched || !variantId) {
      return;
    }

    this.#formatRequest += 1;
    const request = this.#formatRequest;
    this.#materializePackshotImage(format);
    for (const button of this.refs.formatButtons ?? []) {
      button.setAttribute("aria-pressed", String(button.dataset.formatValue === format));
    }

    const variantInput = this.querySelector('product-form-component input[ref="variantId"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }

    const mediaTarget = this.querySelector(`.tea-card__format-media[data-format="${format}"]`);
    for (const link of [this.refs.titleLink, this.refs.mediaLink]) {
      if (link) {
        const url = new URL(link.href, location.origin);
        url.searchParams.set("variant", variantId);
        link.href = url.toString();
        if (link.dataset.vtMediaTarget !== undefined) {
          link.dataset.vtMediaTarget = mediaTarget instanceof HTMLElement ? mediaTarget.id : "";
        }
      }
    }

    this.#applyAvailability(available, format);
    this.#applyFormatMedia(format);

    const pending = this.#pendingFormatImages(format);
    if (pending.length > 0) {
      await Promise.all(pending.map(([image, timeout]) => this.#awaitImage(image, timeout)));
      if (request !== this.#formatRequest || !this.isConnected) {
        return;
      }
    }

    if (this.#hoverMediaRequested) {
      this.#loadLifestyleImage(format);
    }
    this.dataset.activeFormat = format;
  }

  /**
   * The not-yet-decodable images a swap to `format` should wait for, each with its wait cap: the
   * packshot, plus the hover photo when the pointer is on the card (materialized here so it can
   * start loading before the swap rather than after it).
   * @param {string} format
   * @returns {Array<[HTMLImageElement, number]>}
   */
  #pendingFormatImages(format) {
    const hovered = this.#hoverMediaRequested && this.matches(":hover");
    if (hovered) {
      this.#loadLifestyleImage(format);
    }
    const media = this.querySelector(`.tea-card__format-media[data-format="${format}"]`);
    const packshot = media?.querySelector(PACKSHOT.image);
    const lifestyle = hovered ? media?.querySelector(LIFESTYLE.image) : null;
    /** @type {Array<[HTMLImageElement, number]>} */
    const pending = [];
    if (isUnloadedImage(packshot)) {
      pending.push([packshot, PACKSHOT_SWAP_TIMEOUT_MS]);
    }
    if (isUnloadedImage(lifestyle)) {
      pending.push([lifestyle, LIFESTYLE_SWAP_TIMEOUT_MS]);
    }
    return pending;
  }

  /**
   * Resolves once the image is decodable, errored, or the swap timeout passes. The inactive
   * well is `display: none`, where a lazy image never starts loading, so the load is forced.
   * @param {HTMLImageElement} image
   * @param {number} timeout - Milliseconds to wait before proceeding regardless
   */
  async #awaitImage(image, timeout) {
    image.loading = "eager";
    image.setAttribute("fetchpriority", "high");
    await Promise.race([image.decode().catch(() => null), new Promise(resolve => setTimeout(resolve, timeout))]);
  }

  /**
   * Materializes only the requested format's packshot. A template is replaced at most once,
   * so repeated selections and component reconnects cannot duplicate images.
   * @param {string} format
   */
  #materializePackshotImage(format) {
    const media = this.querySelector(`.tea-card__format-media[data-format="${format}"]`);
    if (!(media instanceof HTMLElement)) {
      return;
    }
    const template = media.querySelector(PACKSHOT.template);
    if (!(template instanceof HTMLTemplateElement)) {
      return;
    }
    template.replaceWith(template.content.cloneNode(true));
  }

  /**
   * Activates only the hovered format's inert lifestyle image. Images inside `<template>`
   * do not participate in preload scanning, so touch devices never pay for hover media.
   * @param {string} format
   */
  #loadLifestyleImage(format) {
    const media = this.querySelector(`.tea-card__format-media[data-format="${format}"]`);
    if (!(media instanceof HTMLElement)) {
      return;
    }
    const template = media.querySelector(LIFESTYLE.template);
    if (!(template instanceof HTMLTemplateElement)) {
      const existingImage = media.querySelector(LIFESTYLE.image);
      if (existingImage instanceof HTMLImageElement) {
        this.#trackLifestyleImage(media, existingImage);
      }
      return;
    }
    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const image = fragment.querySelector("img");
    delete media.dataset.lifestyleReady;
    template.replaceWith(fragment);
    if (image instanceof HTMLImageElement) {
      this.#trackLifestyleImage(media, image);
    }
  }

  /**
   * Rebinds readiness listeners after reconnect without adding duplicate listeners during one
   * connection, and re-evaluates readiness every time. A failed decorative lifestyle image is
   * removed without affecting the packshot.
   * @param {HTMLElement} media
   * @param {HTMLImageElement} image
   */
  #trackLifestyleImage(media, image) {
    if (!this.#trackedLifestyleImages.has(image)) {
      this.#trackedLifestyleImages.add(image);
      const revealLifestyle = () => {
        if (image.naturalWidth > 0) {
          media.dataset.lifestyleReady = "";
        }
      };
      image.addEventListener("load", revealLifestyle, { once: true, signal: this.#controller.signal });
      image.addEventListener(
        "error",
        () => {
          delete media.dataset.lifestyleReady;
          image.remove();
        },
        { once: true, signal: this.#controller.signal },
      );
    }

    // Re-checked on every call: a swap that waited for this image must not depend on its `load`
    // event having been delivered before the swap runs.
    if (!image.complete) {
      return;
    }
    if (image.naturalWidth > 0) {
      media.dataset.lifestyleReady = "";
    } else {
      delete media.dataset.lifestyleReady;
      image.remove();
    }
  }

  /**
   * Sold-out formats keep the card browsable but gate the Add button.
   * @param {boolean} available
   * @param {string} format - 'loose' | 'sachets'
   */
  #applyAvailability(available, format) {
    const addButton = this.querySelector('[ref="addToCartButton"]');
    if (!(addButton instanceof HTMLButtonElement)) {
      return;
    }
    addButton.disabled = !available;

    const addLabel = this.querySelector('[ref="addLabel"]');
    if (!addLabel) {
      return;
    }
    // data-add-label is authoritative: a card that RENDERS sold out never shows the Add
    // wording, so capturing the current text (the added-flash pattern) would trap "Sold out".
    const soldOutLabel = this.dataset.soldOutLabel;
    const formatLabel = format === "loose" ? this.dataset.addLabelLoose : this.dataset.addLabelSachets;
    const defaultLabel = formatLabel || this.dataset.addLabel;
    if (!available && soldOutLabel) {
      addLabel.textContent = soldOutLabel;
    } else if (available && defaultLabel) {
      addLabel.textContent = defaultLabel;
    }
  }

  /**
   * Keeps the fly-to-cart animation source on the ACTIVE format's packshot, so
   * otherwise a Loose Leaf add animates the sachet tin.
   * @param {string} format
   */
  #applyFormatMedia(format) {
    const addToCart = this.querySelector("add-to-cart-component");
    if (!(addToCart instanceof HTMLElement)) {
      return;
    }
    const media = format === "loose" ? addToCart.dataset.variantMediaLoose : addToCart.dataset.variantMediaSachets;
    if (media) {
      addToCart.dataset.productVariantMedia = media;
    }
  }

  /**
   * `on:click="/selectFormat/loose"` handler. Resolves once the visual swap has been applied
   * (or skipped for an invalid format).
   * @param {string} format
   * @returns {Promise<void>}
   */
  selectFormat(format) {
    return this.#setFormat(format);
  }
}

/**
 * @param {Element | null | undefined} image
 * @returns {image is HTMLImageElement} An image that is not yet decodable (still loading, or never started)
 */
function isUnloadedImage(image) {
  return image instanceof HTMLImageElement && !(image.complete && image.naturalWidth > 0);
}

/**
 * The inactive format's image source: the inert template's image when it has not been
 * materialized yet, otherwise the live image.
 * @param {Element} media
 * @param {{ template: string, image: string }} kind - PACKSHOT or LIFESTYLE
 * @returns {Element | null}
 */
function imageSource(media, kind) {
  const template = media.querySelector(kind.template);
  return template instanceof HTMLTemplateElement
    ? template.content.querySelector("img")
    : media.querySelector(kind.image);
}

/**
 * A detached low-priority probe that copies the source's responsive attributes verbatim:
 * the rendered card images are never rewritten (the cross-document transition kernel relies
 * on their URLs), and nothing enters the DOM.
 * @param {Element} source
 * @returns {HTMLImageElement}
 */
function createImageProbe(source) {
  const probe = new Image();
  probe.decoding = "async";
  probe.setAttribute("fetchpriority", "low");
  for (const attribute of ["sizes", "srcset", "src"]) {
    const value = source.getAttribute(attribute);
    if (value) {
      probe.setAttribute(attribute, value);
    }
  }
  return probe;
}

/**
 * One shared viewport observer: a card that scrolls near the viewport schedules its
 * inactive packshot prefetch at idle, so touch devices (no hover intent) also get an
 * instant format swap without every card paying for its alternate image up front.
 * @param {TeaCardComponent} card
 */
function observeVisibility(card) {
  if (!("IntersectionObserver" in globalThis)) {
    return;
  }
  visibilityObserver ??= new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          visibilityObserver?.unobserve(entry.target);
          if (entry.target instanceof TeaCardComponent) {
            entry.target.schedulePackshotPrefetch();
          }
        }
      }
    },
    { rootMargin: PREFETCH_ROOT_MARGIN },
  );
  visibilityObserver.observe(card);
}

if (!customElements.get("tea-card-component")) {
  customElements.define("tea-card-component", TeaCardComponent);
}

export { TeaCardComponent };
